import { AppStore } from '../store';
import {
  EChangeType,
  EVariableType,
  ModelInclusion,
  PriorityValue,
  IdFormat,
  ProductScope,
  EJustification,
} from '../data/Constants';
import { createConfigureRequest } from './ConfigurationDataHelperFunctions';

import type {
  IExtendedConfigureResponse,
  IConfigurationVariable,
  ILastChange,
  IValueType,
  IAssignment,
  IConfigurationValue,
  IUserAssignment,
} from '../../types';

/**
 * Creates the request object with the latest assignment after a variable change.
 * Handles assignment addition, removal, and priority logic.
 *
 * @param {IExtendedConfigureResponse} configuration - The configuration object from Redux state.
 * @param {IConfigurationVariable} variable - The variable that was changed.
 * @param {IValueType} value - The new value for the variable.
 * @param {ILastChange} lastChange - The last change object, updated with change details.
 * @returns {object|null} The request object with updated assignments, or null if no change.
 */
export function onVariableChange(
  configuration: IExtendedConfigureResponse,
  variable: IConfigurationVariable,
  value: IValueType,
  lastChange: ILastChange
) {
  // Deep clone assignments array and each assignment object
  const assignmentsCopy =
    configuration.assignments?.map((assignment) => ({
      ...assignment,
    })) || [];
  let currentAssignments: IAssignment[] = getCurrentAssignments(assignmentsCopy);
  let variablePriority = 0;
  const isVariableSubmodel = variable.id.endsWith(IdFormat.Suffix.SubmodelId);
  const config: IExtendedConfigureResponse = AppStore.getState().configuration;
  variablePriority = getPriority(
    variable,
    isVariableSubmodel,
    value,
    config.savedConfiguration?.isServiceConfiguration
  );
  const assignment = currentAssignments.find((a) => a.variableId === variable.id);
  if (findCurrentAssignments(currentAssignments, variable, value)) {
    //same assignment found -> remove assignment
    currentAssignments = currentAssignments.filter(
      (a) => a.variableId !== variable.id || a.value !== value
    );
    lastChange.type = EChangeType.Remove;
  } else if (!variable.allowMultipleAssignments && assignment) {
    //found another assignment for a variable which does not allow multi value -> change value of assignment
    lastChange.valueBefore = assignment.value;
    lastChange.type = EChangeType.Change;
    assignment.value = value;
    if (hasValidPriority(variablePriority, isVariableSubmodel)) {
      assignment.priority = variablePriority;
    }
    currentAssignments = handleEmptyAssignments(currentAssignments, variable, value);
    if (currentAssignments.some((ca) => ca.variableId === assignment.variableId)) {
      const newAssignment: IAssignment = createNewAssignment(
        variable,
        configuration,
        isVariableSubmodel,
        value,
        variablePriority
      );
      lastChange.priority = currentAssignments.find(
        (ca) => ca.variableId === newAssignment.variableId
      )?.priority;
      currentAssignments = currentAssignments.filter((a) => a.variableId !== variable.id);
      currentAssignments.push(newAssignment);
    }
  } else if (value === '') {
    const ruleAssignment = checkAssignedByRule(
      configuration,
      variable,
      isVariableSubmodel,
      lastChange
    );
    if (ruleAssignment) {
      currentAssignments.push(ruleAssignment);
      lastChange.type = EChangeType.Change;
    } else {
      return null;
    }
  } else {
    //no assignment found or variable allows multivalue -> add new assignment
    const newAssignment: IAssignment = createNewAssignment(
      variable,
      configuration,
      isVariableSubmodel,
      value,
      variablePriority
    );
    currentAssignments.push(newAssignment);
    lastChange.type = EChangeType.Add;
    if (currentAssignments.some((ca) => ca.variableId === newAssignment.variableId)) {
      lastChange.priority = currentAssignments.find(
        (ca) => ca.variableId === newAssignment.variableId
      )?.priority;
    }
  }
  currentAssignments = assignPrioritiesToAssigments(currentAssignments);
  return createConfigureRequest(
    configuration.savedConfiguration.modelContext.rootModel.id,
    configuration.configurationId,
    currentAssignments
  );
}

/**
 * Removes assignments for number or submodel variables if the value is zero.
 *
 * @param {IAssignment[]} currentAssignments - Current list of assignments.
 * @param {IConfigurationVariable} variable - The variable being checked.
 * @param {IValueType} value - The value to check.
 * @returns {IAssignment[]} Updated assignments array.
 */
const handleEmptyAssignments = (
  currentAssignments: IAssignment[],
  variable: IConfigurationVariable,
  value: IValueType
) => {
  if (
    (variable.variableType === EVariableType.Number ||
      variable.variableType === EVariableType.Submodel) &&
    Number(value) === 0
  ) {
    return currentAssignments.filter((a) => a.variableId !== variable.id);
  }
  return currentAssignments;
};

/**
 * Finds an assignment for a variable with a specific value in the current assignments array.
 *
 * @param {IAssignment[]} currentAssignments - Current assignments array.
 * @param {IConfigurationVariable} variable - The variable to find.
 * @param {IValueType} value - The value to match.
 * @returns {IAssignment|undefined} The found assignment or undefined.
 */
function findCurrentAssignments(
  currentAssignments: IAssignment[],
  variable: IConfigurationVariable,
  value: IValueType
): IAssignment | undefined {
  return currentAssignments.find((a) => a.variableId === variable.id && a.value === value);
}

/**
 * Returns true if the variable priority is valid (greater than 0 or is a submodel variable).
 *
 * @param {number} variablePriority - The priority value.
 * @param {boolean} isVariableSubmodel - Whether the variable is a submodel.
 * @returns {boolean} True if valid priority.
 */
function hasValidPriority(variablePriority: number, isVariableSubmodel: boolean): boolean {
  return variablePriority > 0 || isVariableSubmodel;
}

/**
 * Returns a copy of the assignments array, setting priority for special variable IDs.
 *
 * @param {IAssignment[]|null} assignments - The assignments array or null.
 * @returns {IAssignment[]} The updated assignments array.
 */
const getCurrentAssignments = (assignments: IAssignment[] | null) => {
  if (assignments) {
    return assignments.map((element) => {
      if (element?.variableId === ModelInclusion.Id) {
        element.priority = PriorityValue.High;
      } else if (element?.variableId === ProductScope.MarketId) {
        element.priority = PriorityValue.Medium;
      } else if (element?.variableId === ProductScope.BuildDateId) {
        element.priority = PriorityValue.Medium;
      }
      return element;
    });
  }
  return [];
};

/**
 * Gets the priority of a variable by checking IsServiceRelevant property and value.
 *
 * @param {IConfigurationVariable} variable - The variable which was changed.
 * @param {boolean} isVariableSubmodel - Whether the variable is a submodel.
 * @param {IValueType} value - The value of the variable.
 * @param {boolean|undefined} isServiceConfiguration - The isServiceConfiguration flag.
 * @returns {number} Priority value (e.g., 999, 0, or normal).
 */
export const getPriority = (
  variable: IConfigurationVariable,
  isVariableSubmodel: boolean,
  value: IValueType,
  isServiceConfiguration: boolean | undefined
) => {
  const serviceRelevant = variable?.pdmProperties?.serviceRelevant ?? false;
  let priority = 0;
  if (isVariableSubmodel && value && (value as number) >= 1) {
    priority = PriorityValue.High;
  } else if (isServiceConfiguration && serviceRelevant) {
    priority = PriorityValue.Normal;
  }
  return priority;
};

/**
 * Assigns priorities to assignments, giving user selections higher priority.
 *
 * @param {IAssignment[]} currentAssignments - The current assignments array.
 * @returns {IAssignment[]} The assignments array with updated priorities.
 */
export const assignPrioritiesToAssigments = (currentAssignments: IAssignment[]) => {
  // filter assignments with Model_Inclusion,Scope, etc
  const otherCurrentAssignments = currentAssignments.filter(
    (currAss) => currAss?.priority && currAss?.priority > 995
  );
  // filter assignments to assign priority on selection & removal
  const leftCurrentAssignments = currentAssignments.filter(
    (currAss) => currAss?.priority === undefined || currAss?.priority < 995
  );
  //Set priority based on the Feature 2892905 to take the user selection as priority and update the configuration and show the conflicts
  leftCurrentAssignments.forEach((curAss, index) => {
    if (index === leftCurrentAssignments.length - 1) {
      curAss.priority = leftCurrentAssignments.length;
    } else {
      curAss.priority = leftCurrentAssignments.length - 1 - index;
    }
  });
  // Merge both filtered assignments array, filter out the
  return [...otherCurrentAssignments, ...leftCurrentAssignments];
};

/**
 * Creates a new assignment object for a variable.
 *
 * @param {IConfigurationVariable} variable - The variable to assign.
 * @param {IExtendedConfigureResponse} configuration - The configuration object.
 * @param {boolean} isVariableSubmodel - Whether the variable is a submodel.
 * @param {IValueType} value - The value to assign.
 * @param {number} variablePriority - The priority to assign.
 * @returns {IAssignment} The new assignment object.
 */
function createNewAssignment(
  variable: IConfigurationVariable,
  configuration: IExtendedConfigureResponse,
  isVariableSubmodel: boolean,
  value: IValueType,
  variablePriority: number
) {
  const newAssignment: IAssignment = {
    type: 'Singleton',
    variableId: variable.id,
    value,
    exclude: false,
  };
  if (configuration.savedConfiguration?.isServiceConfiguration || isVariableSubmodel) {
    newAssignment['priority'] = variablePriority;
  }
  return newAssignment;
}

/**
 * Checks if a variable is assigned by rule and returns a new assignment if so.
 *
 * @param {IExtendedConfigureResponse} configuration - The configuration object.
 * @param {IConfigurationVariable} variable - The variable to check.
 * @param {boolean} isVariableSubmodel - Whether the variable is a submodel.
 * @param {ILastChange} lastChange - The last change object to update.
 * @returns {IAssignment|null} The new assignment if assigned by rule, otherwise null.
 */
function checkAssignedByRule(
  configuration: IExtendedConfigureResponse,
  variable: IConfigurationVariable,
  isVariableSubmodel: boolean,
  lastChange: ILastChange
) {
  let newAssignment: IAssignment | null = null;
  variable.values.forEach((val: IConfigurationValue) => {
    if (val.assigned === EJustification.Rule) {
      lastChange.valueBefore = val.name;
      const value = variable.valueType === EVariableType.Number ? 0 : val.value;
      newAssignment = createNewAssignment(variable, configuration, isVariableSubmodel, value, 1);
    }
  });
  return newAssignment;
}

/**
 * Creates variable assignments from user assignments, ensuring MODEL_INCLUSION is present.
 *
 * @param {IUserAssignment[]} userAssignments - The user assignments array.
 * @returns {IUserAssignment[]} The array of assignments with priorities set.
 */
export const createVariableAssignments = (userAssignments: IUserAssignment[]) => {
  //push MODEL_INCLUSION is assignments array with priority 999 for parent model
  const modelInclusion = userAssignments.find((a) => a.variableId === ModelInclusion.Id);
  if (!modelInclusion) {
    const obj = {
      type: 'Singleton',
      variableId: ModelInclusion.Id,
      value: ModelInclusion.Included,
      exclude: false,
      priority: PriorityValue.High,
    };
    userAssignments = [...userAssignments, obj];
  }
  return userAssignments.map((a: IUserAssignment) => {
    let pr = a.priority && a.priority > 0 ? a.priority : undefined;
    if (a.variableId.endsWith(IdFormat.Suffix.SubmodelId) && Number(a.value) >= 1) {
      pr = PriorityValue.High;
    }
    return {
      type: 'Singleton',
      variableId: a.variableId,
      value: a.value,
      exclude: false,
      priority: pr,
    };
  });
};
